import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import {
  alignItems,
  deviceTypeOptions as allDeviceTypeOptions,
  fileTypes,
  strCode
} from "../../../../../../consts";
import { audienceSetOptions as setOptions } from "../../consts/audienceSetOptions";
import { deviceVersionPatch } from "../../../../consts/deviceVersionPatch";
import { log, notificationService, t } from "../../../../../../services";
import { csvUtil, dropdownUtil } from "../../../../../../utils";

import {
  DropdownInput,
  FileInput,
  FlexContainers,
  RadioGroupSections,
  WrapperContainer
} from "../../../../../../components";

import { deviceModels } from "../../../../consts/deviceModels";
import SelectedItemsList from "../../../../../../components/DropdownMultiselectInput/SelectedItemsList";
import { CurrentFirmwareVersionInput } from "../../../../components/CurrentFirmwareVersionInput/CurrentFirmwareVersionInput";
import { InputSectionWrapper, SectionTitle } from "../../styled";

const allMarketsOption = { id: 0, value: strCode.SELECT_ALL_TITLE };

export const AudienceSection = forwardRef(({ audienceSectionData, isEdit, maxVersion }, ref) => {
  const [selectedDeviceModel, setSelectedDeviceModel] = useState();
  const [selectedDeviceType, setSelectedDeviceType] = useState();

  const [audienceSetOption, setAudienceSetOption] = useState(setOptions.MARKET);

  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [versions, setVersions] = useState([]);

  const [listOfDevicesFile, setListOfDevicesFile] = useState();
  const [dusns, setDusns] = useState([]);

  const [isValid, setIsValid] = useState(true);

  const [showErrors, setShowErrors] = useState(false);
  const [missingFields, setMissingFields] = useState({});

  const deviceModelOptions = useMemo(() => {
    return Object.keys(deviceModels)?.map((device) => {
      return { id: device, value: device };
    });
  }, []);

  // TODO: remove when other device types will be supported
  const deviceTypeOptions = useMemo(() => {
    return allDeviceTypeOptions.filter((opt) => opt.id === "CHARGER");
  }, []);

  const markets = useSelector((store) => store.markets?.markets);
  const marketOptions = useMemo(() => {
    const options = (markets ?? [])?.map((m) => {
      return { id: m.market, value: m.name };
    });

    if (options.length > 1) {
      options.unshift(allMarketsOption);
    }

    return options;
  }, [markets]);

  const onDeviceListUploaded = (file) => {
    setListOfDevicesFile(file);
    setDusns([]);

    if (file) {
      csvUtil.parseCsv(file, onParseFileComplete, onParseFileError);
    }
  };

  const onParseFileComplete = useCallback(
    (results) => {
      if (results?.data?.length) {
        setDusns(results?.data.filter((item) => item.dusn).map((item) => item.dusn));
      } else {
        notificationService.showError({
          title: t(strCode.EMPTY_FILE_ERROR_TITLE),
          message: t(strCode.EMPTY_FILE_ERROR_MESSAGE)
        });
      }
    },
    [setDusns]
  );

  const onParseFileError = useCallback(() => {
    notificationService.showError({
      title: t(strCode.FILE_IMPORT_ERROR_TITLE),
      message: t(strCode.FILE_IMPORT_ERROR_MESSAGE)
    });
  }, []);

  const handleSelectPatch = (patch) => {
    if (!selectedDeviceModel && patch !== "") {
      const deviceModel = Object.keys(deviceVersionPatch).find(
        (key) => deviceVersionPatch[key] === patch
      );

      if (deviceModel) {
        setSelectedDeviceModel(deviceModelOptions.find((opt) => opt.id === deviceModel));
      }
    }
  };

  const onSelectMarket = (id) => {
    // Not "all: option
    if (id) {
      let updatedMarkets = dropdownUtil.updateSelectedOptions(
        id,
        selectedMarkets,
        marketOptions,
        true
      );

      const isAllMarketsSelected =
        updatedMarkets.every((market) => market.id !== allMarketsOption.id) &&
        updatedMarkets.length === marketOptions.length - 1;
      const isAllOptionSelected = updatedMarkets.some(
        (market) => market.id === allMarketsOption.id
      );

      if (isAllMarketsSelected && !isAllOptionSelected) {
        updatedMarkets.push(allMarketsOption);
      }

      if (!isAllMarketsSelected && isAllOptionSelected) {
        updatedMarkets = updatedMarkets.filter((market) => market.id !== allMarketsOption.id);
      }

      setSelectedMarkets(updatedMarkets);
    } else {
      if (selectedMarkets.length === marketOptions.length) {
        setSelectedMarkets([]);
      } else {
        setSelectedMarkets(marketOptions);
      }
    }
  };

  const onDeleteSelectedMarket = (id) => {
    const updatedMarkets = dropdownUtil.updateSelectedOptions(
      id,
      selectedMarkets,
      marketOptions,
      true
    );

    setSelectedMarkets([...updatedMarkets.filter((market) => market.id !== allMarketsOption.id)]);
  };

  const audienceSetOptions = [
    {
      id: setOptions.MARKET,
      value: t(strCode.MARKET_TITLE),
      Component: (
        <WrapperContainer Container={FlexContainers.FlexColumnStart} gap={24} width={"100%"}>
          <DropdownInput
            id="markets-selector"
            title={t(strCode.SELECT_MARKETS_TITLE)}
            placeholder={t(strCode.SELECT_MARKETS_TITLE)}
            options={marketOptions}
            selectedOptions={selectedMarkets ?? []}
            onSelectOption={onSelectMarket}
            hasError={showErrors && missingFields?.selectedMarkets}
            InputComponent={
              selectedMarkets?.length > 0 && (
                <SelectedItemsList
                  items={selectedMarkets}
                  onDelete={onDeleteSelectedMarket}
                  checkIsOptionDisabled={(id) => !marketOptions?.some((market) => market.id === id)}
                />
              )
            }
            showCheckboxes
          />

          <CurrentFirmwareVersionInput
            versions={versions}
            setVersions={setVersions}
            selectedDevice={selectedDeviceModel?.id}
            maxVersion={maxVersion}
            handleSelectPatch={handleSelectPatch}
            isOptional
          />
        </WrapperContainer>
      )
    },
    {
      id: setOptions.DEVICE_LIST,
      value: t(strCode.LIST_OF_DEVICES_TITLE),
      Component: (
        <FileInput
          id={"list-of-devices-file"}
          title={t(strCode.LIST_OF_DEVICES_FILE_TITLE)}
          file={listOfDevicesFile}
          setFile={onDeviceListUploaded}
          fileType={fileTypes.csv}
          hasError={showErrors && missingFields?.listOfDevicesFile}
        />
      )
    }
  ];

  const getAudienceSectionData = useCallback(() => {
    return {
      selectedDeviceModel,
      selectedDeviceType,
      audienceSetOption,
      selectedMarkets,
      versions,
      listOfDevicesFile,
      dusns
    };
  }, [
    selectedDeviceModel,
    selectedDeviceType,
    audienceSetOption,
    selectedMarkets,
    versions,
    listOfDevicesFile,
    dusns
  ]);

  useEffect(() => {
    prepopulateData();

    return () => {
      resetFormData();
    };
  }, [audienceSectionData]);

  useEffect(() => {
    let valid = !!selectedDeviceModel && !!selectedDeviceType;

    const isDeviceListOption = audienceSetOption === setOptions.DEVICE_LIST;

    if (isDeviceListOption) {
      valid = valid && !!listOfDevicesFile && dusns?.length;
    } else {
      valid = valid && !!selectedMarkets?.length;
    }

    const errorFields = {
      selectedDeviceModel: !selectedDeviceModel,
      selectedDeviceType: !selectedDeviceType,
      selectedMarkets: !isDeviceListOption && !selectedMarkets?.length,
      listOfDevicesFile: isDeviceListOption && (!listOfDevicesFile || !dusns?.length)
    };

    setMissingFields(errorFields);
    setIsValid(valid);
  }, [
    selectedDeviceModel,
    selectedDeviceType,
    audienceSetOption,
    selectedMarkets,
    listOfDevicesFile,
    dusns
  ]);

  const prepopulateData = useCallback(() => {
    if (audienceSectionData) {
      setSelectedDeviceModel(audienceSectionData?.selectedDeviceModel);
      setSelectedDeviceType(audienceSectionData?.selectedDeviceType);

      setAudienceSetOption(audienceSectionData?.audienceSetOption);

      setSelectedMarkets(
        audienceSectionData?.selectedMarkets?.length > 1 &&
          audienceSectionData?.selectedMarkets?.length !== marketOptions.length - 1
          ? audienceSectionData?.selectedMarkets
          : [allMarketsOption, ...(audienceSectionData?.selectedMarkets || [])]
      );
      setVersions(audienceSectionData?.versions);

      setListOfDevicesFile(audienceSectionData?.listOfDevicesFile);
      setDusns(audienceSectionData?.dusns);
    }
  }, [audienceSectionData]);

  const resetFormData = () => {
    setSelectedDeviceModel(null);
    setSelectedDeviceType(null);

    setAudienceSetOption(setOptions.MARKET);

    setSelectedMarkets([]);
    setVersions([]);

    setListOfDevicesFile(null);
    setDusns([]);
  };

  useImperativeHandle(ref, () => ({
    saveAudienceSectionData(validate = true) {
      if (!showErrors && validate) {
        setShowErrors(true);
      }

      if (!isValid && validate) {
        log.warn("AudienceSection: Unable to save audience section due to missing fields");
        notificationService.showError({
          title: t(strCode.MISSING_FIELDS),
          message: t(strCode.MISSING_FIELDS_MESSAGE)
        });
        return;
      }

      return getAudienceSectionData();
    }
  }));

  return (
    <WrapperContainer
      Container={FlexContainers.FlexColumnStart}
      alignItems={alignItems.start}
      gap={16}
      width={"100%"}
      height={"100%"}>
      <SectionTitle>{t(strCode.AUDIENCE_TITLE)}</SectionTitle>

      <InputSectionWrapper mt={8}>
        <DropdownInput
          id="device-model-selector"
          title={t(strCode.DEVICE_MODEL_TITLE)}
          placeholder={t(strCode.DEVICE_MODEL_PLACEHOLER)}
          options={deviceModelOptions}
          selectedOption={selectedDeviceModel}
          onSelectOption={(id) => {
            setSelectedDeviceModel(deviceModelOptions.find((opt) => opt.id === id));
          }}
          hasError={showErrors && missingFields?.selectedDeviceModel}
          disabled={isEdit}
        />

        <DropdownInput
          id="device-type-selector"
          title={t(strCode.DEVICE_TYPE_TITLE)}
          placeholder={t(strCode.DEVICE_TYPE_PLACEHOLER)}
          options={deviceTypeOptions}
          selectedOption={selectedDeviceType}
          onSelectOption={(id) => {
            setSelectedDeviceType(deviceTypeOptions.find((opt) => opt.id === id));
          }}
          hasError={showErrors && missingFields?.selectedDeviceType}
          disabled={isEdit}
        />
      </InputSectionWrapper>

      <InputSectionWrapper>
        <RadioGroupSections
          title={t(strCode.SET_AUDIENCE_TITLE)}
          name="audience-set-options"
          options={audienceSetOptions}
          selectedId={audienceSetOption}
          onChange={setAudienceSetOption}
        />
      </InputSectionWrapper>
    </WrapperContainer>
  );
});

AudienceSection.displayName = "AudienceSection";

AudienceSection.propTypes = {
  audienceSectionData: PropTypes.shape({
    selectedDeviceModel: PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    }).isRequired,
    selectedDeviceType: PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    }).isRequired,
    audienceSetOption: PropTypes.string.isRequired,
    selectedMarkets: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.string.isRequired, value: PropTypes.string.isRequired })
    ),
    versions: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.string.isRequired, value: PropTypes.string.isRequired })
    ),
    listOfDevicesFile: PropTypes.object,
    dusns: PropTypes.arrayOf(PropTypes.string)
  }),
  isEdit: PropTypes.bool.isRequired,
  maxVersion: PropTypes.string
};
